import { useNavigate, useParams } from '@tanstack/react-router'
import type { DealFlowMatchData } from '@workspace/api-backend'
import { ConnectDialog, ProfileView } from '@workspace/seedforge-features/profile'
import { formatAmount, raiseStageLabel, stageLabel } from '@workspace/seedforge-shared'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FullScreenState } from '@workspace/ui/components/full-screen-state'
import { getInitials } from '@workspace/ui/lib/utils'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { InvestorLayout } from '../../components/investor-layout'
import { VideoPreviewModal } from '../../components/video-preview-modal'
import { CustomDiligenceDialog } from '../../features/deal-flow/components/custom-diligence-dialog'
import { DocumentPreviewModal } from '../../features/preview/components/document-preview-modal'
import {
  useConnectDealFlowMatchMutation,
  useDealFlowMatchDocumentDownloadMutation,
  useDealFlowMatchQuery,
  useDealFlowMatchVideoUrlMutation,
} from '../../hooks/api'
import { useDocumentPreview } from '../../hooks/use-document-preview'
import { useVideoPreview } from '../../hooks/use-video-preview'

type Dialog = 'connect' | 'diligence' | null

export function DealFlowStartupPage() {
  const { id, startupId } = useParams({ strict: false }) as { id: string; startupId: string }
  const navigate = useNavigate()
  const matchQuery = useDealFlowMatchQuery(id, startupId)
  const [dialog, setDialog] = useState<Dialog>(null)
  const connectMutation = useConnectDealFlowMatchMutation(id)
  const downloadMutation = useDealFlowMatchDocumentDownloadMutation()
  const videoUrlMutation = useDealFlowMatchVideoUrlMutation()
  const docPreview = useDocumentPreview({
    fetchDocumentUrl: async (documentId) => {
      const res = await downloadMutation.mutateAsync({
        path: { investorId: id, startupId, documentId },
      })
      if (res.status === 'ok') return res.data.downloadUrl
      throw new Error('Failed to get download URL')
    },
  })

  const startup = matchQuery.data?.data
  const pitchSessionId = startup?.profile.pitchSessionId ?? null
  const video = useVideoPreview({
    fetchId: pitchSessionId,
    fetchVideoUrl: (_id) =>
      videoUrlMutation.mutateAsync({ path: { investorId: id, startupId } }).then((r) => r.data.url),
  })

  const goBack = () => navigate({ to: '/app/investor/$id/deal-flow', params: { id } })

  if (matchQuery.isLoading) {
    return (
      <InvestorLayout activeNav="Deal Flow">
        <FullScreenState loading title="Loading startup..." />
      </InvestorLayout>
    )
  }

  return (
    <InvestorLayout activeNav="Deal Flow">
      <div className="mx-auto max-w-6xl py-2">
        <div className="mb-4 flex items-center justify-between gap-2">
          <SecondaryButton size="sm" onClick={goBack}>
            <ArrowLeft className="size-3.5" />
            Back to Deal Flow
          </SecondaryButton>
          {/* The investor's own diligence has its own page; the Profile itself
              carries no evaluation. */}
          {startup?.customEvaluation?.status === 'COMPLETED' &&
            startup.customEvaluation.evaluationId && (
              <SecondaryButton
                size="sm"
                onClick={() =>
                  navigate({
                    to: '/app/investor/$id/evaluation/$evaluationId',
                    params: { id, evaluationId: startup.customEvaluation?.evaluationId as string },
                  })
                }
              >
                View your custom diligence
              </SecondaryButton>
            )}
        </div>

        {startup ? (
          <ProfileView
            {...toProfileViewProps(startup)}
            videoUrl={video.videoUrl ?? undefined}
            onPlayVideo={video.handlePlayRecording}
            onConnect={() => setDialog('connect')}
            onRequestDiligence={() => setDialog('diligence')}
            onDocumentClick={(doc) => {
              if (doc.documentId) {
                docPreview.openDocument({
                  id: doc.documentId,
                  fileName: doc.name,
                  mimeType: doc.mimeType ?? null,
                })
              }
            }}
          />
        ) : (
          <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
            <h2 className="text-lg font-semibold text-foreground">Startup not found</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              This startup is no longer in your deal flow.
            </p>
          </div>
        )}
      </div>

      {startup && dialog === 'connect' && (
        <ConnectDialog
          startupName={startup.profile.startup.name}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          onConnect={(message) => {
            connectMutation.mutate({
              path: { investorId: id, startupId },
              body: { message: message || null },
            })
            setDialog(null)
          }}
          isPending={connectMutation.isPending}
        />
      )}
      {startup && dialog === 'diligence' && (
        <CustomDiligenceDialog
          investorId={id}
          startupId={startup.profile.startupId}
          startupName={startup.profile.startup.name}
          open
          onOpenChange={(open) => !open && setDialog(null)}
        />
      )}
      <DocumentPreviewModal
        document={docPreview.previewDoc}
        open={docPreview.previewOpen}
        onOpenChange={docPreview.setPreviewOpen}
        fetchUrl={docPreview.fetchDocumentUrl}
      />
      {video.videoUrl && (
        <VideoPreviewModal
          videoUrl={video.videoUrl}
          title="Pitch Recording"
          open={video.videoOpen}
          onOpenChange={video.setVideoOpen}
          startTime={video.videoStartTime}
          onTimeUpdate={video.setVideoResumeTime}
        />
      )}
    </InvestorLayout>
  )
}

function formatFileSize(size: number | null): string {
  if (size === null) return ''
  if (size >= 1_048_576) return `${(size / 1_048_576).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

function toProfileViewProps(match: DealFlowMatchData) {
  const { profile } = match
  const { startup, evaluation, pitchSessionId, teamMembers, documents, updates } = profile

  const pitchRecordings =
    evaluation && pitchSessionId
      ? [
          {
            id: pitchSessionId,
            duration: evaluation.pitchDuration || '30 min',
            readinessLabel: '',
            recordedDate: String(evaluation.date),
            verdictSnippet: '',
            highlights: [],
            judgeCount: 0,
            topStrength: '',
            topConcern: '',
          },
        ]
      : []

  return {
    profile: {
      initials: getInitials(startup.name),
      name: startup.name,
      sector: startup.categoryName ?? '',
      sectorLabel: startup.categoryName ?? undefined,
      stage: stageLabel(startup.productStage),
      raisingStatus: startup.isFundraising,
      location: startup.location ?? '',
      founded: startup.foundingDate ?? '',
      website: startup.websiteUrl ?? '',
      description: startup.description ?? '',
      tags: startup.tags,
    },
    fundraising: {
      round: raiseStageLabel(startup.fundraisingStage),
      target: formatAmount(startup.fundraisingAmount) ?? '',
    },
    pitchRecordings,
    selectedPitchId: pitchSessionId ?? '',
    updates: updates.map((u) => ({ id: u.id, date: String(u.date), text: u.text })),
    documents: documents.map((d) => ({
      documentId: d.id,
      name: d.label || d.fileName,
      size: formatFileSize(d.fileSize),
      type: d.type,
      mimeType: d.mimeType,
      uploadStatus: 'ready' as const,
    })),
    team: teamMembers.map((member) => ({
      id: member.id,
      initials: getInitials(member.name),
      name: member.name,
      role: member.position ?? '',
      linkedInUrl: member.linkedinUrl ?? undefined,
      imageUrl: member.imageUrl,
      companyLogos: member.companyLogos,
      lastEducation: member.lastEducation,
      linkedinScrapeStatus: member.linkedinScrapeStatus,
    })),
    isPublic: true,
    startupName: startup.name,
  }
}
